import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../../common/user";
import { transfersService } from "../../../../services/admin/transfers.service";
import { OrderType } from "../../../../components/affiliate/Trackers";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../../common/ui";

export type payloadList = {
  page: number;
  perPage?: number;
  order: OrderType;
  filter: {
    search?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    recipientType?: string | null;
    operationType?: string | null;
    from?: number[] | string[] | null;
    toAgent?: number[] | string[] | null;
    toPlayer?: number[] | string[] | null;
    //wallet?: any | null;
    wallet?: { operator: any; brand: any; currency: any; } | undefined | null;
  }
};

export type payloadTransactions = {
  transferId: number;
};

export type listItem = {
  id: number;
  username: string;
  status: string;
  type: string;
  email: string;
  createdAt: string;
  level: number;
  allSubAgents: number;
  directSubAgents: number;
  playersCount: number;
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
  startRowNum: number;
  endRowNum: number;
  totalEntries: number | string;
  totalPages: number;
}

export interface TransactionItem {
  participant: string;
  balanceBefore: string;
  balanceAfter: string;
}

export interface ITransactionsData {
  transferId: number;
  transactions: TransactionItem[];
}

export type transactionsSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  transactions: {
    data: ITransactionsData | null,
    error: any,
    isFetching: boolean,
  },
  download: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  options: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  allTransferslist: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  allTransactions: {
    data: ITransactionsData | null,
    error: any,
    isFetching: boolean,
  },
  allDownload: {
    data: any,
    error: any,
    isFetching: boolean,
  },
};

const initialState: transactionsSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  transactions: {
    data: null,
    error: null,
    isFetching: false,
  },
  download: {
    data: null,
    error: null,
    isFetching: false,
  },
  options: {
    data: null,
    error: null,
    isFetching: false,
  },
  allTransferslist: {
    data: null,
    error: null,
    isFetching: false,
  },
  allTransactions: {
    data: null,
    error: null,
    isFetching: false,
  },
  allDownload: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'agent/transfers/getListData',
  async (payload: payloadList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await transfersService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getTransactionsByIdData: any = createAsyncThunk(
  'agent/transfers/getTransactionsById',
  async (payload: payloadTransactions, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await transfersService.getTransactionsById(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'error',
          message: 'error',
          messageKey: `${response.status} - ${response.statusText}`,
        }));
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return {
        transferId: payload.transferId,
        transactions: data.transactions,
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const downloadTransfersListData: any = createAsyncThunk(
  'agent/transfers/downloadTransfersListData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await transfersService.downloadTransfersList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getTransfersOptionsData: any = createAsyncThunk(
  'agent/transfers/getTransfersOptionsData',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await transfersService.getTransfersOptions();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getAllTransfersListData: any = createAsyncThunk(
  'agent/transfers/getAllTransfersListData',
  async (payload: payloadList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await transfersService.getAllTransfersList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getAllTransactionsByIdData: any = createAsyncThunk(
  'agent/transfers/getAllTransactionsByIdData',
  async (payload: payloadTransactions, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await transfersService.getAllTransactionsById(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'error',
          message: 'error',
          messageKey: `${response.status} - ${response.statusText}`,
        }));
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return {
        transferId: payload.transferId,
        transactions: data.transactions,
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const downloadAllTransfersListData: any = createAsyncThunk(
  'agent/transfers/downloadAllTransfersListData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await transfersService.downloadAllTransfersList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const transfersSlice = createSlice({
  name: 'transfers',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearTransactionsState(state) {
      state.transactions.error = null;
      state.transactions.isFetching = false;
      state.transactions.data = null;
    },
    clearDownloadState(state) {
      state.download.error = null;
      state.download.isFetching = false;
      state.download.data = null;
    },
    clearOptionsState(state) {
      state.options.error = null;
      state.options.isFetching = false;
      state.options.data = null;
    },
    clearAllTransfersListState(state) {
      state.allTransferslist.error = null;
      state.allTransferslist.isFetching = false;
      state.allTransferslist.data = null;
    },
    clearAllTransactionsState(state) {
      state.allTransactions.error = null;
      state.allTransactions.isFetching = false;
      state.allTransactions.data = null;
    },
    clearAllDownloadState(state) {
      state.allDownload.error = null;
      state.allDownload.isFetching = false;
      state.allDownload.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<transactionsSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

    builder.addCase(getTransactionsByIdData.pending, (state ) => {
      state.transactions.isFetching = true;
      state.transactions.error = null;
    });
    builder.addCase(getTransactionsByIdData.fulfilled, (state , action) => {
      state.transactions.error = null;
      state.transactions.isFetching = false;
      state.transactions.data = action.payload;
    });
    builder.addCase(getTransactionsByIdData.rejected, (state , action) => {
      state.transactions.isFetching = false;
      state.transactions.error = action.payload;
      state.transactions.data = null;
    });

    builder.addCase(getAllTransfersListData.pending, (state ) => {
      state.allTransferslist.isFetching = true;
      state.allTransferslist.error = null;
    });
    builder.addCase(getAllTransfersListData.fulfilled, (state , action) => {
      state.allTransferslist.error = null;
      state.allTransferslist.isFetching = false;
      state.allTransferslist.data = action.payload;
    });
    builder.addCase(getAllTransfersListData.rejected, (state , action) => {
      state.allTransferslist.isFetching = false;
      state.allTransferslist.error = action.payload;
      state.allTransferslist.data = null;
    });

    builder.addCase(getAllTransactionsByIdData.pending, (state ) => {
      state.allTransactions.isFetching = true;
      state.allTransactions.error = null;
    });
    builder.addCase(getAllTransactionsByIdData.fulfilled, (state , action) => {
      state.allTransactions.error = null;
      state.allTransactions.isFetching = false;
      state.allTransactions.data = action.payload;
    });
    builder.addCase(getAllTransactionsByIdData.rejected, (state , action) => {
      state.allTransactions.isFetching = false;
      state.allTransactions.error = action.payload;
      state.allTransactions.data = null;
    });

    builder.addCase(downloadTransfersListData.pending, (state) => {
      state.download.isFetching = true;
      state.download.error = null;
    });
    builder.addCase(downloadTransfersListData.fulfilled, (state, action) => {
      state.download.error = null;
      state.download.isFetching = false;
      state.download.data = action.payload;
    });
    builder.addCase(downloadTransfersListData.rejected, (state, action) => {
      state.download.isFetching = false;
      state.download.error = action.payload;
      state.download.data = null;
    });

    builder.addCase(getTransfersOptionsData.pending, (state) => {
      state.options.isFetching = true;
      state.options.error = null;
    });
    builder.addCase(getTransfersOptionsData.fulfilled, (state, action) => {
      state.options.error = null;
      state.options.isFetching = false;
      state.options.data = action.payload;
    });
    builder.addCase(getTransfersOptionsData.rejected, (state, action) => {
      state.options.isFetching = false;
      state.options.error = action.payload;
      state.options.data = null;
    });

    builder.addCase(downloadAllTransfersListData.pending, (state) => {
      state.allDownload.isFetching = true;
      state.allDownload.error = null;
    });
    builder.addCase(downloadAllTransfersListData.fulfilled, (state, action) => {
      state.allDownload.error = null;
      state.allDownload.isFetching = false;
      state.allDownload.data = action.payload;
    });
    builder.addCase(downloadAllTransfersListData.rejected, (state, action) => {
      state.allDownload.isFetching = false;
      state.allDownload.error = action.payload;
      state.allDownload.data = null;
    });
  },
});
export const {
  clearListState,
  clearTransactionsState,
  clearDownloadState,
  clearOptionsState,
  clearAllTransfersListState,
  clearAllTransactionsState,
  clearAllDownloadState,
} = transfersSlice.actions;
export default transfersSlice.reducer;


